import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import * as VisuallyHidden from '@radix-ui/react-visually-hidden'

import { X } from 'lucide-react'
import CommonSearchInput from './CommonSearchInput'
import { CommonModalType } from '@/utils/interfaces'
import { dispatch, useSelector } from '@/lib/store'
import { updateCartData } from '@/lib/reducers/cartReducer'
import { Fragment } from 'react'
import TestAndPackagesListSkeleton from './Skeleton/TestAndPackagesListSkeleton'
import { CHECKOUT_PAGE, EDOS_PAGE } from '@/utils/constants'
import PACKAGE_HELPER from '@/helper/packageHelper'
import ActionButton from './ActionButton'
import TestAndPackagesCard from './TestAndPackagesCard'
import usePopularHealthCheckupsAndSearchProducts from '@/hooks/usePopularHealthCheckupsAndSearchProducts'

const SearchTestsAndPackagesModal = ({
  open,
  onOpenChange,
}: CommonModalType) => {
  const { handleCheckValidationForProduct } = PACKAGE_HELPER()

  const {
    pathname,
    mostPopularPackage: MOST_POPULAR_PACKAGE,
    mostPopularTest: MOST_POPULAR_TEST,
    searchQuery,
    setSearchQuery,
    searchQueryProductData,
    loading,
    showFlag,
    setShowFlag,
    debounceSearch,
  } = usePopularHealthCheckupsAndSearchProducts({ loadPopular: true })

  const cartState: any = useSelector((state) => state.cart) || {}
  const CART_ITEMS = cartState?.cartDtoList[0]?.productDtoList || []

  const frequentlyBookedTests =
    MOST_POPULAR_PACKAGE?.length || MOST_POPULAR_TEST?.length
      ? [...MOST_POPULAR_PACKAGE, ...MOST_POPULAR_TEST]
      : []

  const PRODUCT_DATA_TO_MAP =
    (!searchQuery && searchQueryProductData?.length == 0
      ? frequentlyBookedTests
      : searchQueryProductData) || []

  const SHOW_CART_BUTTON =
    CART_ITEMS?.filter(
      (item1: any) =>
        PRODUCT_DATA_TO_MAP?.length > 0 &&
        PRODUCT_DATA_TO_MAP?.some(
          (item2: any) => item1.id === item2?.productDto?.id
        )
    )?.length > 0

  const handleGoToCart = () => {
    if (pathname !== CHECKOUT_PAGE) {
      dispatch(updateCartData({ ...cartState, modalIsOpen: true }))
    }
    onOpenChange(false)
  }

  return (
    <Dialog open={open} defaultOpen={true} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby="Search for tests and packages"
        showCloseButton={false}
        className="location_prompt_modal rounded-none sm:rounded-xl p-0 gap-0 h-full sm:h-[700px] sm:max-h-[90vh] transition-all border-none flex flex-col overflow-hidden max-w-[580px] shadow-elevation4"
      >
        <DialogHeader className="pt-4 px-5 pb-2 border-b border-solid border-gray-200 shadow-elevation3">
          <VisuallyHidden.Root>
            <DialogTitle className="m-0 p-0">
              Search for tests and packages
            </DialogTitle>
            <DialogDescription>Search for tests and packages</DialogDescription>
          </VisuallyHidden.Root>
          <div className="flex gap-4 items-center">
            <CommonSearchInput
              autoFocus
              value={searchQuery}
              placeholder={
                pathname === EDOS_PAGE
                  ? 'Search by test name / test code'
                  : 'Search for tests and packages'
              }
              onChange={(e) => setSearchQuery(e.target.value)}
              containerClassName="w-full"
            />
            <DialogClose>
              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                <X size={20} className="text-gray-800" />
              </div>
            </DialogClose>
          </div>
        </DialogHeader>
        {!loading && (
          <Fragment>
            <div className="flex-1 overflow-y-auto bg-white-100">
              {(!debounceSearch || debounceSearch.length < 3) && (
                <Fragment>
                  <div className="px-5 pt-4 pb-2.5 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary-400" />
                      <p className="text-label-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Popular Packages
                      </p>
                    </div>
                  </div>

                  <div>
                    {!!MOST_POPULAR_PACKAGE &&
                      MOST_POPULAR_PACKAGE?.length > 0 &&
                      MOST_POPULAR_PACKAGE.slice(0, 5)?.map(
                        (product: any, index: number) => {
                          const productData = product?.productDto
                          const validationResponse =
                            handleCheckValidationForProduct(product)
                          const validationErrorToMap: any = validationResponse
                          return (
                            <TestAndPackagesCard
                              key={index}
                              product={product}
                              productDto={productData}
                              onOpenChange={onOpenChange}
                              validationErrorToMap={validationErrorToMap}
                              setShowFlag={setShowFlag}
                            />
                          )
                        }
                      )}
                  </div>
                  <div className="px-5 pt-4 pb-2.5 bg-gray-50 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-secondary-500" />
                      <p className="text-label-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Popular Tests
                      </p>
                    </div>
                  </div>

                  <div>
                    {!!MOST_POPULAR_TEST &&
                      MOST_POPULAR_TEST?.length > 0 &&
                      MOST_POPULAR_TEST?.slice(0, 5)?.map(
                        (product: any, index: number) => {
                          const productData = product?.productDto
                          const validationResponse =
                            handleCheckValidationForProduct(product)
                          const validationErrorToMap: any = validationResponse
                          return (
                            <TestAndPackagesCard
                              key={index}
                              product={product}
                              productDto={productData}
                              onOpenChange={onOpenChange}
                              validationErrorToMap={validationErrorToMap}
                              setShowFlag={setShowFlag}
                            />
                          )
                        }
                      )}
                  </div>
                </Fragment>
              )}
              {!!debounceSearch && (
                <div>
                  {!!searchQueryProductData &&
                    searchQueryProductData?.length > 0 &&
                    searchQueryProductData?.map(
                      (product: any, index: number) => {
                        const productData = product?.productDto
                        const validationResponse =
                          handleCheckValidationForProduct(product)
                        const validationErrorToMap: any = validationResponse
                        return (
                          <TestAndPackagesCard
                            key={index}
                            product={product}
                            productDto={productData}
                            onOpenChange={onOpenChange}
                            validationErrorToMap={validationErrorToMap}
                            setShowFlag={setShowFlag}
                          />
                        )
                      }
                    )}
                  {!!debounceSearch &&
                    debounceSearch.length >= 3 &&
                    PRODUCT_DATA_TO_MAP?.length === 0 && (
                      <div className="py-16 px-6 flex flex-col items-center justify-center text-center">
                        <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="text-gray-400"
                          >
                            <circle cx="11" cy="11" r="8" />
                            <path d="m21 21-4.3-4.3" />
                            <path d="m8 11 6 0" />
                          </svg>
                        </div>
                        <p className="text-body-sm font-semibold text-gray-800">
                          No results found
                        </p>
                        <p className="text-body-xs text-gray-700 mt-1 max-w-[280px]">
                          We couldn&apos;t find anything for &quot;
                          {debounceSearch}&quot;. Try a different search term.
                        </p>
                      </div>
                    )}
                </div>
              )}
            </div>
          </Fragment>
        )}
        {!!loading && <TestAndPackagesListSkeleton />}
        {showFlag && cartState?.activePatientDetailDto?.firstName && (
          <div className="bg-primary-50 py-2.5 text-center border-t border-primary-100">
            <p className="text-label-xs text-primary-600">
              Adding for{' '}
              <span className="text-label-xs">
                {cartState?.activePatientDetailDto?.firstName}
              </span>
            </p>
          </div>
        )}
        {SHOW_CART_BUTTON && (
          <div className="py-3 px-5 border-t border-gray-200 bg-white-100">
            <ActionButton
              variant="primary"
              title="Go to cart"
              className="w-full text-label-md font-semibold overflow-hidden rounded-lg"
              onClick={handleGoToCart}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default SearchTestsAndPackagesModal
